/*
 * Money and time formatting for the order screens.
 *
 * Two things this centralises that were previously done inline, differently,
 * in four places:
 *
 * 1. Money. `n.toFixed(2) + " SAR"` produced "9999.00 SAR" — no thousands
 *    separator, currency repeated in every cell of a column. Amounts here are
 *    grouped and the currency is named once by the caller (a column header, a
 *    total row), so a column of figures compares by shape.
 *
 * 2. Time. The API hands back a pre-formatted GMT string
 *    ("Thu, 30 Jul 2026 12:09:48 GMT"). Rendering it raw showed Riyadh staff a
 *    time three hours behind the one they placed the order at. Everything here
 *    is converted to Asia/Riyadh, which is the only clock this business runs on.
 */

export const CURRENCY = 'SAR';
export const ORDER_TIME_ZONE = 'Asia/Riyadh';

const EM_DASH = '—';

const amountFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat('en-US');

/*
 * 12-hour clock, hours and minutes only.
 *
 * The API sends seconds ("2026-07-30 12:09:48") and nobody reconciles an order
 * to the second — they read the time to know roughly when it happened, and the
 * extra digits are noise in a column you scan vertically.
 *
 * en-GB gives day-first ordering, which is how dates are written here, but
 * lowercases the meridiem to "pm". `normaliseMeridiem` uppercases it so the
 * column doesn't mix cases with anything else on the screen.
 *
 * `hourCycle: 'h12'`, not `hour12: true`: for en-GB the latter picks the h11
 * cycle, which prints the hour after noon and after midnight as 0 —
 * "0:05 PM" for five past twelve.
 */
const dateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hourCycle: 'h12',
  timeZone: ORDER_TIME_ZONE,
});

const shortDateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
  hourCycle: 'h12',
  timeZone: ORDER_TIME_ZONE,
});

const normaliseMeridiem = (value) =>
  value.replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase());

/** Numeric value or null — never NaN, so callers can branch on absence. */
export const toNumber = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** "9,999.00" — grouped, two decimals, no currency. Em dash when absent. */
export const formatAmount = (value, { fallback = EM_DASH } = {}) => {
  const n = toNumber(value);
  return n == null ? fallback : amountFormatter.format(n);
};

/** "8" / "1,240" — counts and quantities. */
export const formatCount = (value) => {
  const n = toNumber(value);
  return n == null ? EM_DASH : integerFormatter.format(n);
};

/**
 * The API's `created_on` is an RFC-1123 string, which `Date` parses natively.
 * Anything unparseable returns null rather than an Invalid Date, so callers
 * fall back to printing the raw value instead of the string "Invalid Date".
 */
export const parseOrderDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** "30 Jul 2026, 12:09 PM" in Riyadh. Falls back to the raw string. */
export const formatDateTime = (value) => {
  const date = parseOrderDate(value);
  if (!date) return value || EM_DASH;
  return normaliseMeridiem(dateTimeFormatter.format(date));
};

/** "30 Jul, 12:09 PM" — for rows where the year is redundant. */
export const formatShortDateTime = (value) => {
  const date = parseOrderDate(value);
  if (!date) return value || EM_DASH;
  return normaliseMeridiem(shortDateTimeFormatter.format(date));
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "just now" / "12 min ago" / "6 days ago". Returns null when the timestamp is
 * unparseable or in the future, so the caller can simply omit the hint rather
 * than print something odd like "-3 days ago".
 */
export const formatRelative = (value, now = Date.now()) => {
  const date = parseOrderDate(value);
  if (!date) return null;
  const delta = now - date.getTime();
  if (delta < 0) return null;
  if (delta < MINUTE) return 'just now';
  if (delta < HOUR) {
    const mins = Math.floor(delta / MINUTE);
    return `${mins} min ago`;
  }
  if (delta < DAY) {
    const hours = Math.floor(delta / HOUR);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  const days = Math.floor(delta / DAY);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
};

/** "1 item · 3 units" — the count line on the items card. */
export const formatItemCount = (itemCount, unitCount) => {
  const items = `${formatCount(itemCount)} item${itemCount === 1 ? '' : 's'}`;
  const units = `${formatCount(unitCount)} unit${unitCount === 1 ? '' : 's'}`;
  return `${items} · ${units}`;
};
